import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { CursorModelDeveloperBreakdownPoint, CursorToolSummary, ReportingCoverage } from '@ace-tracker/shared';
import { chartAxisTickProps, chartLegendProps, chartTooltipProps, chartXAxisTickProps } from '../chartTheme';
import { CURSOR_METRIC_INFO } from '../metricInfo';
import { DeveloperName } from '../settings';
import { ChartCard } from './ChartCard';
import { EfficiencyGauge } from './EfficiencyGauge';
import { ExportMenu } from './ExportMenu';
import { InfoTooltip } from './InfoTooltip';
import { KpiCard } from './KpiCard';

/**
 * The Cursor IDE body of the dashboard (preview: the collector is off by
 * default until validated on a real install). Token figures are Cursor's own
 * counts, not yet verified, and are never priced.
 */
export function CursorDashboardBody({
  toolData,
  developersCount,
  coverage,
  SectionHeading,
}: {
  toolData: CursorToolSummary;
  developersCount: number;
  coverage: ReportingCoverage;
  SectionHeading: (props: { title: string; hint?: string; info?: string }) => JSX.Element;
}) {
  const grouped = groupCursorTimeSeries(toolData.timeSeries);
  const inputTokens = toolData.byModel.reduce((sum, m) => sum + m.inputTokens, 0);
  const outputTokens = toolData.byModel.reduce((sum, m) => sum + m.outputTokens, 0);
  const { requests, requestsWithTokens } = toolData.activity;
  const tokenCoverage = requests > 0 ? Math.round((requestsWithTokens / requests) * 100) : 0;

  return (
    <>
      <p className="flex items-start gap-2 rounded-card border border-border bg-warn-bg px-4 py-3 text-xs text-text-default">
        <i aria-hidden="true" className="fa-solid fa-triangle-exclamation mt-0.5 text-warn-ink" />
        <span>
          <strong className="font-medium">Preview.</strong> The Cursor collector reads Cursor&apos;s local database in a layout taken from
          public research and has not yet been checked against a real install. Treat these figures as indicative.
        </span>
      </p>

      <section>
        <SectionHeading title="Headline metrics" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          <KpiCard
            label="Requests"
            value={requests.toLocaleString()}
            meta="Completed prompts in range"
            info={CURSOR_METRIC_INFO.requests}
            hero
            accentClassName="bg-primary-blue"
          />
          <KpiCard
            label="Conversations"
            value={toolData.activity.sessions.toLocaleString()}
            meta="Distinct Cursor conversations"
            info={CURSOR_METRIC_INFO.sessions}
            accentClassName="bg-sky-blue"
          />
          <KpiCard
            label="Active minutes"
            value={toolData.activity.activeMinutes.toLocaleString()}
            meta="Merged bursts of activity"
            info={CURSOR_METRIC_INFO.activeMinutes}
            accentClassName="bg-sky-blue"
          />
          <KpiCard
            label="Active developers"
            value={String(developersCount)}
            meta={`${coverage.reportingDevices} of ${coverage.expectedDevices} devices reporting`}
            info={CURSOR_METRIC_INFO.activeDevelopers}
            accentClassName="bg-sky-blue"
          />
          <KpiCard
            label="Reported tokens"
            value={(inputTokens + outputTokens).toLocaleString()}
            meta={`${inputTokens.toLocaleString()} in · ${outputTokens.toLocaleString()} out`}
            flag="Unverified"
            info={CURSOR_METRIC_INFO.tokens}
            accentClassName="bg-info"
          />
          <KpiCard
            label="Tool calls"
            value={toolData.activity.toolCalls.toLocaleString()}
            meta={`${Math.round(toolData.activity.toolErrorRate * 1000) / 10}% refused or failed`}
            info={CURSOR_METRIC_INFO.toolCalls}
            accentClassName="bg-info"
          />
        </div>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <EfficiencyGauge
            label="Token coverage"
            value={tokenCoverage}
            valueLabel={requests > 0 ? `${tokenCoverage}%` : '—'}
            meta={`${requestsWithTokens.toLocaleString()} of ${requests.toLocaleString()} requests report tokens`}
            info={CURSOR_METRIC_INFO.tokenCoverage}
            size="sm"
          />
          <EfficiencyGauge
            label="Reporting coverage"
            value={coverage.coveragePercent}
            valueLabel={`${coverage.coveragePercent}%`}
            meta={`${coverage.reportingDevices} of ${coverage.expectedDevices} devices, last 48h`}
            info={CURSOR_METRIC_INFO.reportingCoverage}
            size="sm"
          />
        </div>
        <p className="mt-3 flex items-start gap-1.5 rounded-card border border-border bg-surface-alt px-4 py-3 text-xs text-text-muted">
          <span>
            <strong className="font-medium text-text-default">No cost shown.</strong> Cursor bills through its own plans and its token counts
            are not yet verified, so Cursor is not included in Spend or any cost total.
          </span>
          <InfoTooltip text={CURSOR_METRIC_INFO.noCost} />
        </p>
      </section>

      <section>
        <SectionHeading title="Trends & breakdown" />
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
          <ChartCard
            title="Engagement trend"
            caption="Requests, conversations and tool calls per day, corrected to max() per device"
            info={CURSOR_METRIC_INFO.engagementTrend}
            height="h-96"
            className="lg:col-span-7"
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={grouped}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--line-soft)" />
                <XAxis dataKey="date" {...chartXAxisTickProps} />
                <YAxis {...chartAxisTickProps} />
                <Tooltip {...chartTooltipProps} />
                <Legend {...chartLegendProps} />
                <Bar dataKey="requests" fill="#0058AB" name="Requests" />
                <Bar dataKey="sessions" fill="#06D6A0" name="Conversations" />
                <Bar dataKey="toolCalls" fill="#00B4D8" name="Tool calls" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            title="Usage by model"
            caption="Completed requests per model"
            info={CURSOR_METRIC_INFO.usageByModel}
            height="h-96"
            className="lg:col-span-5"
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={toolData.byModel}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--line-soft)" />
                <XAxis dataKey="model" {...chartXAxisTickProps} />
                <YAxis {...chartAxisTickProps} />
                <Tooltip {...chartTooltipProps} />
                <Bar dataKey="events" fill="#0058AB" name="Requests" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      </section>

      <section>
        <CursorDrillDownTable breakdown={toolData.modelDeveloperBreakdown} info={CURSOR_METRIC_INFO.drillDown} />
      </section>
    </>
  );
}

function CursorDrillDownTable({ breakdown, info }: { breakdown: CursorModelDeveloperBreakdownPoint[]; info?: string }) {
  const sorted = [...breakdown].sort((a, b) => (a.model === b.model ? b.events - a.events : a.model.localeCompare(b.model)));

  return (
    <div data-export-root className="rounded-card border border-border bg-surface p-5 shadow-sm">
      <ExportMenu title="Model x developer breakdown" className="float-right -mr-2 -mt-2 ml-2" />
      <h3 className="flex items-center gap-1.5 text-sm font-medium text-text-default">
        Model x developer breakdown
        {info && <InfoTooltip text={info} />}
      </h3>
      <p className="mt-1 text-xs text-text-muted">Every (model, developer) pair with usage in this range. Token figures are unverified.</p>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[760px] text-left text-sm">
          <thead className="border-b border-border text-[11px] uppercase tracking-wide text-text-faint">
            <tr>
              <th className="pb-2.5 pr-3">Model</th>
              <th className="pb-2.5 pr-3">Developer</th>
              <th className="pb-2.5 pr-3 text-right">Requests</th>
              <th className="pb-2.5 pr-3 text-right">Input tokens</th>
              <th className="pb-2.5 pr-3 text-right">Output tokens</th>
              <th className="pb-2.5 text-right">Tool calls</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((row) => (
              <tr key={`${row.model}:${row.developerId}`} className="border-b border-border last:border-0">
                <td className="py-2.5 pr-3 text-text-default">{row.model}</td>
                <td className="py-2.5 pr-3">
                  <DeveloperName developerId={row.developerId} />
                </td>
                <td className="py-2.5 pr-3 text-right tabular-nums">{row.events}</td>
                <td className="py-2.5 pr-3 text-right tabular-nums">{row.inputTokens.toLocaleString()}</td>
                <td className="py-2.5 pr-3 text-right tabular-nums">{row.outputTokens.toLocaleString()}</td>
                <td className="py-2.5 text-right tabular-nums">{row.toolCalls.toLocaleString()}</td>
              </tr>
            ))}
            {sorted.length === 0 && (
              <tr>
                <td className="py-3 text-text-muted" colSpan={6}>
                  No usage in this range.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function groupCursorTimeSeries(timeSeries: { date: string; metricName: string; value: number }[]) {
  const byDate = new Map<string, { date: string; requests: number; sessions: number; toolCalls: number }>();
  for (const point of timeSeries) {
    const row = byDate.get(point.date) ?? { date: point.date, requests: 0, sessions: 0, toolCalls: 0 };
    if (point.metricName === 'cursor.requests') row.requests = point.value;
    if (point.metricName === 'cursor.sessions') row.sessions = point.value;
    if (point.metricName === 'cursor.tool.calls') row.toolCalls = point.value;
    byDate.set(point.date, row);
  }
  return Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date));
}
